/*
 * Copyright (c) 2021 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import {
  dispatchCorrectActionIfChildOrRemote,
  getActionPayload,
  mergeCurrentModalContentToTarget,
  processDataToDispatchErrorAction
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import * as StateManager from '../../store/state/state-manager';
import * as actionType from '../../store/state/actions';
import Constants, { publicConstants } from '../../constants';
import Utils from '../../utils/utils';
import { onErrorApplyErrorStateAndMergeButtons, isCaseResolved } from '../../utils/case-utils';
import { ActionManagerInstance } from '../../actions/manager';
import ActionsSequencer from '../../actions/sequencer';
import {
  getCaseClassName,
  getCaseInfoID,
  getDisplayCaseID,
  updateKeyAndFlowName,
  setLastRefreshTimeProperty,
  getContainerNameFromContainerItemID,
  isNextStepInCreateStage,
  deferCaseSummaryViewUpdate,
  getCaseInfo
} from '../common-helper';
import { publishEndOfAssignmentProcessingEvent } from '../common-events';
import { getSemanticURL } from '../../router/app-router';
import {
  getDataContextName,
  getTargetFromContainerItemID,
  getActiveContainerItemName,
  getChildContainers,
  isContainerSingleMode
} from '../../container/container-utils';
import { getParentContextName } from '../../utils/context-utils';
import EnvironmentInfo from '../../environment-info/index';
import { isFormValid } from '../../utils/form-handler';
import { clearMessages, isCustomPageErrorExist } from '../../messages';
import DataApi from '../../data-view';
import type { Action } from '../../store/state/state-manager';
import type {
  ToastMessage,
  Headers,
  ChainedDataPayload,
  DxAPIResponseObject,
  BlendedUICreateStageCompletedInfo
} from '../types';

import { FINISH_ASSIGNMENT } from './action-creator';
import { executeFinishAssignmentRequest } from './async-request';
import {
  addActionToActivateModalContainerItem,
  getPayloadForEndOfAssignmentProcessingEvent,
  addRemoveTransientItemAction,
  isModalLaunchedFromPrimary,
  canUpdateAssignmentData,
  addActionsForFinishAssignmentLaunchedFromModal,
  publishEventsForLocalActionFromPreviewView,
  addActionsForFinishAssignmentOfNonCreateStages,
  createToastMessage,
  processFinishAssignmentWithOpenAssignment,
  canPerformCurrentAssignment,
  mutateErroneousFields
} from './helper';
import {
  publishExpressLocalActionSubmitEvent,
  publishCreateStageDoneEvent,
  publishErrorOnFinishAssignment,
  publishLocalActionSubmit
} from './events';

const { RESOURCE_TYPES } = publicConstants;

export const finishAssignment = (action$: Observable<Action>, state$: { value: any }) => {
  let actionMgrID = 0;
  let actionsOut: any[] = [];
  let toastMessage: ToastMessage = {};
  let actionContext = '';
  let blendedUICreateStageCompletedInfo: BlendedUICreateStageCompletedInfo = {};
  let isACaseWideAction = false;
  let isProcessAction = false;

  return action$.pipe(
    ofType(FINISH_ASSIGNMENT),
    // eslint-disable-next-line sonarjs/cognitive-complexity
    mergeMap((action: Action) => {
      const isCoexistenceUIKITApp = EnvironmentInfo.isCoexistenceAppOfType(Constants.APP_TYPE.UIKIT);
      ({ actionMgrID } = action.payload);
      actionContext = action.payload.context;
      clearMessages({
        context: actionContext,
        stateObj: StateManager.getDataObject(actionContext),
        clearContextMessages: true
      });
      if (!isFormValid(actionContext) || isCustomPageErrorExist(actionContext)) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        ActionsSequencer.handleDeferredActionCompletion(action.payload.context).then(() => {
          publishErrorOnFinishAssignment({ errorType: publicConstants.ERROR_TYPE.CLIENT });
        });
        // return empty observable as form is not valid.
        return of([]);
      }
      return of(action.payload).pipe(
        mergeMap((payload) => {
          // To identify the current opened modal in ModalViewContainer, we are activating current container on every finishAssignment
          addActionToActivateModalContainerItem(actionsOut, payload);
          const mutatingPayload = { ...payload };
          return executeFinishAssignmentRequest(mutatingPayload, { state$ });
        }),
        mergeMap(
          async (chainedData: {
            type: string;
            context: string;
            headers: Headers;
            actionInPayload: Action['payload'];
            response: DxAPIResponseObject;
            payload: ChainedDataPayload;
          }) => {
            if (chainedData && chainedData.type === actionType.ERROR) {
              onErrorApplyErrorStateAndMergeButtons(chainedData);

              const errorAction = processDataToDispatchErrorAction(chainedData);
              actionsOut.push(errorAction);
              return actionsOut;
            }
            // clear DataApi caches after Submit is success
            DataApi.clearContextedCache(actionContext);
            const { headers, actionInPayload, response } = chainedData;
            const {
              containerItemID,
              context: actionInContext = getDataContextName(containerItemID),
              isModalAction,
              isInCreateStage
            } = actionInPayload;
            isACaseWideAction = actionInPayload.isCaseWideAction;
            isProcessAction = actionInPayload.isProcessAction;
            const target = getTargetFromContainerItemID(containerItemID);
            actionInPayload.target = target;

            const containerName = getContainerNameFromContainerItemID(containerItemID);
            // this is the current container info from where the action got triggered, we shouldn't alter this object

            const currentContainerInfo = Object.freeze({
              target: actionInPayload.target,
              containerItemID,
              context: actionInContext,
              containerName
            });

            /* When a finish assignment is done, it is no more first assignment in the flow. */
            Utils.setIn(response.data, Constants.CASE_INFO.IS_FIRST_ASSIGNMENT, false);
            Utils.setIn(response.data, Constants.CASE_INFO_IS_LOCAL_ACTION, false);
            if (response?.uiResources?.root?.config?.name && actionInPayload.viewType !== 'page') {
              Utils.setIn(response.data, Constants.CASE_INFO.VIEW_NAME, response?.uiResources?.root?.config?.name);
            }

            publishExpressLocalActionSubmitEvent({ submitResponse: response });

            let translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
              [publicConstants.STOPMERGEACTIONS]: !response.nextAssignmentInfo
            });
            /* Use stopMergeActions to restrict to display localActions in Actions Menu in Review Mode. */
            const caseClassName = getCaseClassName(translatedDXAPIResponse);
            const parentContainerItemID = getParentContextName(actionInPayload.context);
            const activeWorkArea = getChildContainers(parentContainerItemID)[0]
              ? getActiveContainerItemName(getChildContainers(parentContainerItemID)[0])
              : parentContainerItemID;
            const isNextStepInCreateStageVariable = isNextStepInCreateStage(translatedDXAPIResponse);
            const isLastAssignmentInCreateStage = isInCreateStage && !isNextStepInCreateStageVariable;

            if (
              isModalAction &&
              !isInCreateStage &&
              !isModalLaunchedFromPrimary(actionInPayload.context, activeWorkArea)
            ) {
              translatedDXAPIResponse = mergeCurrentModalContentToTarget(
                translatedDXAPIResponse,
                StateManager.getDataObject(containerItemID)
              );
            }

            /* commonOutActionPayload will get mutated down the process, based on use cases. */
            const commonOutActionPayload = getActionPayload(translatedDXAPIResponse, {
              headers,
              actionMgrID,
              directUrlUpdation: false
            });
            /* lastRefreshTime property will be consumed by files & attachments widget or any other to get refreshed per every finish assignment submit */
            setLastRefreshTimeProperty(commonOutActionPayload.data, actionInPayload);
            commonOutActionPayload.caseClassName = caseClassName;
            commonOutActionPayload.resourceType = RESOURCE_TYPES.ASSIGNMENT;
            commonOutActionPayload.headers = headers;
            let keyValue;
            let isNextAssignmentInfoPresent;
            const validAssignment = translatedDXAPIResponse?.data?.caseInfo?.assignments?.some(
              (assignment: { ID?: string }) => assignment.ID === actionInPayload.assignmentID
            );
            const canPerform = canPerformCurrentAssignment(translatedDXAPIResponse, actionInPayload);
            const canUpdateAssignment = canUpdateAssignmentData(
              validAssignment,
              activeWorkArea,
              translatedDXAPIResponse,
              isModalAction,
              canPerform
            );
            // Moving assignments to Review mode for Transfer assignment action to maintain consistency
            const isActionTransferAssignment =
              actionInPayload?.actionID === publicConstants.LOCAL_ACTIONS.TRANSFER_ASSIGNMENT;
            if (translatedDXAPIResponse?.data?.nextAssignmentInfo && !isActionTransferAssignment) {
              commonOutActionPayload.caseViewMode = Constants.PERFORM_MODE;
              keyValue = getCaseInfoID(translatedDXAPIResponse);
              isNextAssignmentInfoPresent = true;
            } else if (canUpdateAssignment && !isActionTransferAssignment) {
              /**
               * Use case : Finish assignment of case-wide action with active assignment/task open in the background
               * when nextAssignmentInfo is not available(eg: other than Edit details)
               */
              if (
                translatedDXAPIResponse?.confirmationNote &&
                translatedDXAPIResponse?.uiResources?.root?.config?.name === Constants.PY_CONFIRM
              ) {
                commonOutActionPayload.caseViewMode = Constants.REVIEW_MODE;
              } else {
                commonOutActionPayload.caseViewMode = Constants.PERFORM_MODE;
              }
              keyValue = getCaseInfoID(translatedDXAPIResponse);
              commonOutActionPayload.flowName = ' ';
              commonOutActionPayload.deleteAssignmentSpecificResources = false;
              isNextAssignmentInfoPresent = false;
            } else {
              commonOutActionPayload.caseViewMode = Constants.REVIEW_MODE;
              const isContainerSingleModeValue = isContainerSingleMode(getTargetFromContainerItemID(activeWorkArea));
              const caseInfo = getCaseInfo(translatedDXAPIResponse);
              const isCaseResolvedValue = isCaseResolved(caseInfo);
              if (
                translatedDXAPIResponse?.confirmationNote &&
                (isCaseResolvedValue ||
                  translatedDXAPIResponse?.uiResources?.root?.config?.name === Constants.PY_CONFIRM) &&
                !isContainerSingleModeValue
              ) {
                keyValue = getCaseInfoID(translatedDXAPIResponse);
              } else {
                keyValue = '';
              }
              isNextAssignmentInfoPresent = false;
            }
            const caseInfoID = getCaseInfoID(translatedDXAPIResponse);
            updateKeyAndFlowName(commonOutActionPayload, translatedDXAPIResponse, {
              skipFlowNameUpdate: commonOutActionPayload?.caseViewMode === Constants.REVIEW_MODE || canUpdateAssignment
            });
            const semanticURL = getSemanticURL('createWork', { caseClassName }).replace(
              /{.+?}/,
              String(getDisplayCaseID(caseInfoID))
            );
            translatedDXAPIResponse.semanticURL = semanticURL;
            commonOutActionPayload.semanticURL = semanticURL;

            if (isModalAction) {
              await addActionsForFinishAssignmentLaunchedFromModal(
                actionInPayload,
                commonOutActionPayload,
                isNextAssignmentInfoPresent,
                keyValue,
                translatedDXAPIResponse,
                actionsOut,
                chainedData,
                response,
                false,
                blendedUICreateStageCompletedInfo
              );
              if (isLastAssignmentInCreateStage) {
                toastMessage = createToastMessage(actionInPayload);
              }
            } else if (isInCreateStage && isCoexistenceUIKITApp && !isNextStepInCreateStageVariable) {
              /* change view from embed assignment to  pyDetails */
              EnvironmentInfo.setDefaultCasePage('pyDetails');

              await processFinishAssignmentWithOpenAssignment(
                response,
                actionsOut,
                getParentContextName(actionInContext)
              );
            } else {
              await addActionsForFinishAssignmentOfNonCreateStages(
                commonOutActionPayload,
                actionInPayload,
                translatedDXAPIResponse,
                currentContainerInfo,
                actionsOut
              );
            }
            deferCaseSummaryViewUpdate(chainedData, actionsOut);
            ActionsSequencer.handleDeferredActionCompletion(actionContext).catch(() => {});
            // Use Case: 2 ==> Local action finish assignment for below use cases- a. local action from preview view
            publishEventsForLocalActionFromPreviewView(commonOutActionPayload, actionInPayload, activeWorkArea);

            // publish end of assignment processing event
            if (!translatedDXAPIResponse?.data?.nextAssignmentInfo) {
              const eventPayload = getPayloadForEndOfAssignmentProcessingEvent(
                translatedDXAPIResponse,
                actionInPayload
              );
              publishEndOfAssignmentProcessingEvent({ ...eventPayload, containerItemID });
            }

            addRemoveTransientItemAction(actionsOut, {
              transientItemID: containerItemID,
              target,
              isModalAction
            });

            return actionsOut;
          }
        ),
        map(() => {
          const errorObject = actionsOut.find((obj) => obj.type === actionType.ERROR);
          if (errorObject) {
            ActionsSequencer.handleDeferredActionCompletion(actionContext)
              .then(() => {
                publishErrorOnFinishAssignment({ errorType: publicConstants.ERROR_TYPE.SERVER });
              })
              .catch(() => {});
            mutateErroneousFields(errorObject);
            ActionManagerInstance.rejectActionByID(actionMgrID);
          } else {
            ActionManagerInstance.resolveActionByID(actionMgrID, toastMessage);
          }
          /* publish create stage info to infinity for case tracking */
          if (blendedUICreateStageCompletedInfo?.caseId) {
            publishCreateStageDoneEvent(blendedUICreateStageCompletedInfo);
          }
          dispatchCorrectActionIfChildOrRemote(actionsOut);
          if (isACaseWideAction || isProcessAction) {
            publishLocalActionSubmit({ updateState: true });
          }
          actionsOut = [];
          toastMessage = {};
          blendedUICreateStageCompletedInfo = {};
          return actionsOut;
        })
      );
    })
  );
};

export default finishAssignment;
